import { styled, TextField } from '@mui/material';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-2),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
        '.Mui-focused ': {
          ' & .MuiOutlinedInput-notchedOutline': {
            opacity: 1,
          },
        },
      }
    : {},
);

export const TextFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  onTextBlur,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  onTextBlur: () => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation('translation');

  const [hasFocus, setHasFocus] = useState(false);

  const handleBlur = () => {
    setHasFocus(false);
    onTextBlur();
  };

  if (fieldConfiguration.fieldType !== 'TEXT') return;

  const rows = fieldConfiguration.rows ?? 3;
  const multiline = fieldConfiguration.multiline ?? false;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues([event.target.value]);
  };

  if (multiline) {
    return (
      <StyledTextField
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - text field redacted"
        data-testid={'fim3-custom-field-input-text-input'}
        isEditableText={isEditableText}
        value={values[0]}
        onChange={onChange}
        onFocus={() => setHasFocus(true)}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        multiline
        minRows={rows}
        maxRows={isEditableText && !hasFocus ? 1 : undefined}
        placeholder={isEditableText ? '' : t(($) => $.intake.general.textFieldPlaceholder)}
        size="small"
        slotProps={{
          htmlInput: {
            maxLength: fieldConfiguration.maxLength ?? undefined,
            'data-testid': 'fim3-custom-field-input-text-input-field',
          },
        }}
        {...rest}
      />
    );
  } else {
    return (
      <StyledTextField
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - text field redacted"
        data-testid={'fim3-custom-field-input-text-input'}
        isEditableText={isEditableText}
        value={values}
        fullWidth
        onChange={onChange}
        onFocus={() => {
          setHasFocus(true);
        }}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
          }
        }}
        placeholder={isEditableText ? '' : t(($) => $.intake.general.textFieldPlaceholder)}
        size="small"
        slotProps={{
          htmlInput: {
            maxLength: fieldConfiguration.maxLength,
            'data-testid': 'fim3-custom-field-input-text-input-field',
          },
        }}
        {...rest}
      />
    );
  }
};
